import React from 'react';

/**
 * Presentational "now playing + next transition" banner (Scheduling Pro). Driven by the
 * shared status payload ({ schedule:{ now, next, timezone } }) the backend computes with
 * ScheduleStatusService. Used by the café Theme Timeline to reach wall parity; the wall
 * keeps its own i18n-wired ScheduleStatusBanner.
 *
 * Café semantics: now.source === 'none' means nothing is scheduled right now, so the
 * device's DEFAULT theme is showing (not an error).
 */
const SOURCE_ICON = { window: 'pi-calendar', default: 'pi-bookmark', fallback: 'pi-clock', none: 'pi-circle' };

const DAY = {
  today: 'today', tomorrow: 'tomorrow',
  Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun',
};

export default function ScheduleNowNextBanner({
  status, loading = false, uniform = true,
  noneLabel = 'Default theme is playing (nothing scheduled now).',
  nowLabel = (name) => `Now: ${name}`,
  nextLabel = (name, at, day) => `Next: ${name} at ${at} (${day})`,
  nextNone = 'No upcoming change in the next 7 days.',
  notUniform = 'Screens in this group are in different timezones — times shown are approximate.',
  loadingLabel = 'Loading…',
}) {
  const schedule = status?.schedule;
  const now = schedule?.now;
  const next = schedule?.next;
  const dayOf = (d) => DAY[d] || d;

  return (
    <div className="p-3 mb-3" style={{
      background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)',
      borderRadius: 'var(--pp-radius-lg)', borderInlineStart: '4px solid var(--pp-primary)',
    }}>
      {loading ? (
        <small style={{ color: 'var(--pp-text-secondary)' }}>{loadingLabel}</small>
      ) : !now || now.source === 'none' ? (
        <div>
          <div style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
            <i className="pi pi-circle me-2" style={{ color: 'var(--pp-text-secondary)' }} />
            {noneLabel}
          </div>
          {next && (
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              {nextLabel(next.playlist_name || '—', next.at, dayOf(next.at_day))}
            </small>
          )}
        </div>
      ) : (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
              <i className={`pi ${SOURCE_ICON[now.source] || 'pi-play'} me-2`} style={{ color: 'var(--pp-success)' }} />
              {nowLabel(now.playlist_name || '—')}
              {now.window && (
                <span className="ms-2" style={{ fontWeight: 500, fontSize: 12, color: 'var(--pp-text-secondary)' }}>
                  {now.window.start}–{now.window.end}
                </span>
              )}
            </div>
            {next ? (
              <small style={{ color: 'var(--pp-text-secondary)' }}>
                <i className="pi pi-arrow-right me-1" style={{ fontSize: 10 }} />
                {nextLabel(next.playlist_name || '—', next.at, dayOf(next.at_day))}
              </small>
            ) : (
              <small style={{ color: 'var(--pp-text-secondary)' }}>{nextNone}</small>
            )}
          </div>
          {schedule?.timezone && (
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              <i className="pi pi-clock me-1" />{schedule.timezone}
            </small>
          )}
        </div>
      )}
      {!uniform && (
        <small className="d-block mt-1" style={{ color: 'var(--pp-warning)' }}>
          <i className="pi pi-exclamation-triangle me-1" />{notUniform}
        </small>
      )}
    </div>
  );
}
